import { FilterMatch, ProductMatch, SegmentPresetKey, WindowPeriod } from '../listCategory/type';
import { Day, OpeningHours } from '../stores/models/openingWeek.Model';
import { PosCatalogItemKind } from '../stores/type';

export interface Multiplicator {
  id: string;
  storeIds: string[];
  coefficient: number;
  slots: Day[];
  hours: OpeningHours[];
}

export interface MultiplicatorHoursDTO {
  start: string;
  end: string;
}
export interface SlotDTO {
  day: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
  hours: MultiplicatorHoursDTO[];
}

export interface MultiplicatorDTO {
  storeIds: string[];
  coefficient: number;
  slots: SlotDTO[];
}
export type LoyaltyProduct = {
  id: string;
  name: string;
  category?: string | null;
  // Absent on items picked before this existed; the list they are in is what they meant.
  kind?: PosCatalogItemKind | null;
};

export interface LoyaltyModelWithoutOffersDTO {
  id: string;
  franchiseName: string;
  franchiseLogo: string;
  loyaltyType: LoyaltyType;
  franchiseId: string;
  affiliatedStoreIds: string[];
  tagScanInterval: number;
  selectedProductList?: LoyaltyProduct[];
  selectedExtrasList?: LoyaltyProduct[];
  maxPoints?: number;
  multiplicators?: MultiplicatorDTO[];
  blockPointsOnDiscount?: boolean;
}

export interface LoyaltyModelDetailDTO extends LoyaltyModelWithoutOffersDTO {
  productCount: number;
  // Absent from a backend that predates the imported catalogue.
  importedProductCount?: number;
  discountReportingSupported?: boolean;
}

export type ImportedCatalogueProduct = {
  id: string;
  name: string;
  category?: string | null;
};

export type CatalogueImportRejectionReason = 'missing_id' | 'missing_name' | 'duplicate_id';

export type ProductCatalogueImportReport = {
  importedCount: number;
  rejectedRows: { row: number; id: string | null; reason: CatalogueImportRejectionReason }[];
  selectedIdsMissing: string[];
};

export type ProductCataloguePage = {
  items: ImportedCatalogueProduct[];
  total: number;
  page: number;
  limit: number;
  missingSelectedIds: string[];
};

export interface LoyaltyModelEntity extends LoyaltyModelDetailDTO {
  loyaltyOffers: LoyaltyOfferDTO[];
  posProductIds: string[];
}

export interface UpdateLoyaltyModelDTO extends Omit<LoyaltyModelWithoutOffersDTO, 'affiliatedStoreIds'> {
  hasMaxPointsEnabled: boolean;
  blockPointsOnDiscount: boolean;
  posProductIds: string[];
  _loyaltyOffers?: LoyaltyOfferDTO[];
}

export interface LoyaltyOfferDTO {
  id: string;
  image?: string;
  description: string;
  redeemPointAmount: number;
  dateCreated: string;
  lastModified: string;
  storeListId: string;
  type: LoyaltyOfferType;
  status: LoyaltyOfferStatus;
  statusUpdated: string;
  redeemedCount: number;
  lastRedeemed: string;
  loyaltyProducts: LoyaltyProduct[];
  loyaltyExtras?: LoyaltyProduct[];
  productPriority?: ProductPriorityEnum;
  value?: number;
  usageLimit?: OfferUsageLimitDTO | null;
  remainingUses?: number | null;
  usageLimitReached?: boolean | null;
  availableAgainAt?: string | null;
  requiredSegmentId?: string | null;
  badge?: OfferBadgeDTO | null;
  locked?: boolean | null;
  badgeMissing?: boolean | null;
  losesBadges?: OfferBadgeRefDTO[] | null;
  redeemSlots?: OfferRedeemSlotsDTO | null;
  redeemSlotOpen?: boolean | null;
  slotClosesAt?: string | null;
  nextSlotOpensAt?: string | null;
  upcomingSlots?: OfferUpcomingSlotDTO[] | null;
  redeemSlotsEnded?: boolean | null;
}

export type OfferSlotDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export type OfferSlotHoursDTO = {
  start: string;
  end: string;
};

export type OfferWeeklySlotDTO = {
  days: OfferSlotDay[];
  hours: OfferSlotHoursDTO[];
};

export type OfferOneOffSlotDTO = {
  start: string;
  end: string;
};

export type OfferRedeemSlotsDTO = {
  weekly: OfferWeeklySlotDTO[];
  oneOff: OfferOneOffSlotDTO[];
};

export type OfferUpcomingSlotDTO = {
  opensAt: string;
  closesAt: string;
};

// Form values. Weekly hours are "HH:MM" wall clock, an end of "00:00" meaning midnight (sent as "24:00").
// `key` identifies a row across removals; it is never sent.
export type OfferWeeklySlotForm = OfferWeeklySlotDTO & {
  key: string;
};

// One-off bounds are "YYYY-MM-DDTHH:mm" wall clock in the franchise zone, null until picked.
export type OfferOneOffSlotForm = {
  key: string;
  start: string | null;
  end: string | null;
};

export type OfferRedeemSlotsForm = {
  weekly: OfferWeeklySlotForm[];
  oneOff: OfferOneOffSlotForm[];
};

export interface OfferBadgeRefDTO {
  segmentId: string;
  label: string | null;
  emoji: string | null;
}

export type OfferBadgeConditionDisplay = 'progress' | 'countdown' | 'deadline';

export interface OfferBadgeConditionDTO {
  key: string;
  unit: string;
  display: OfferBadgeConditionDisplay;
  min: number | null;
  max: number | null;
  current: number | null;
  met: boolean;
  remaining: number | null;
  period: WindowPeriod | null;
  days: number | null;
  at: string | null;
  closest: boolean;
  productNames: string[] | null;
  productMatch: ProductMatch | null;
}

export interface OfferBadgeGroupDTO {
  match: FilterMatch;
  met: boolean;
  conditions: OfferBadgeConditionDTO[];
}

export interface OfferBadgeDTO extends OfferBadgeRefDTO {
  presetKey: SegmentPresetKey | null;
  held: boolean;
  surprise: boolean;
  match: FilterMatch;
  groups: OfferBadgeGroupDTO[];
}

export interface FlatLoyaltyOfferDTO extends LoyaltyOfferDTO {
  type: LoyaltyOfferType.FLAT;
  value: number;
  currency: string;
}

export interface PercentageLoyaltyOfferDTO extends LoyaltyOfferDTO {
  type: LoyaltyOfferType.PERCENTAGE;
  value: number;
}

export interface ProductLoyaltyOfferDTO extends LoyaltyOfferDTO {
  type: LoyaltyOfferType.PRODUCT;
  loyaltyProducts: LoyaltyProduct[];
  productPriority?: ProductPriorityEnum;
}

export interface LoyaltyOffersInDTO {
  loyaltyOffers: (FlatLoyaltyOfferDTO | PercentageLoyaltyOfferDTO | ProductLoyaltyOfferDTO | LoyaltyOfferDTO)[];
}

export enum LoyaltyCardTransactionEvent {
  tag = 'tag',
  redeem = 'redeem',
  credit = 'credit',
  debit = 'debit',
  sync = 'sync',
  migration = 'migration',
  emarketing = 'emarketing',
}

export enum LoyaltyOfferType {
  PRODUCT = 'PRODUCT',
  FLAT = 'FLAT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum OfferUsagePeriod {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  TOTAL = 'TOTAL',
}

export interface OfferUsageLimitDTO {
  count: number;
  period: OfferUsagePeriod;
}

export interface OfferUsageLimitForm {
  count: string;
  period: OfferUsagePeriod;
}

export enum ProductPriorityEnum {
  lowPrice = 'LOW_PRICE',
  highPrice = 'HIGH_PRICE',
}

export enum LoyaltyOfferStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum LoyaltyType {
  PASSAGE = 'PASSAGE',
  MANUAL_AMOUNT = 'MANUAL_AMOUNT',
  POS_AMOUNT = 'POS_AMOUNT',
  POS_PRODUCTS = 'POS_PRODUCTS',
  POS_PRODUCTS_AMOUNT = 'POS_PRODUCTS_AMOUNT',
  POS_PASSAGE = 'POS_PASSAGE',
}

export interface LoyaltyCardTransaction {
  id: string;
  date: string;
  event: LoyaltyCardTransactionEvent;
  pointsChange: number;
  storeId: string;
}

export interface LoyaltyCardTransactionInDTO extends LoyaltyCardTransaction {
  userId: string;
  loyaltyCardId: string;
  franchiseId: string;
  loyaltyModelId: string;
  amount?: number;
  loyaltyOfferId?: string;
  receiptId?: string;
  merchantId?: string;
  lbOrderId?: string;
  gameTransactionId?: string;
  formResponseId?: string;
  storeName: string;
  initialPoints?: number;
  multiplier?: number;
}

export interface LoyaltyCardInDTO {
  id: string;
  userId: string;
  dateCreated: string;
  lastModified: string;
  isActive: boolean;
  lastTagScan: string;
  loyaltyModelId: string;
  isNewClient?: boolean;
  points: number;
  totalCollectedPoints: number;
  loyaltyCardTransactions: LoyaltyCardTransaction[];
  averageBasket: number;
  weeklyAverageBasket: number;
  note?: string;
}

export interface LoyaltyCardUpdateDTO {
  id: string;
  note?: string;
}

export interface RedeemOfferDTO {
  loyaltyCard: LoyaltyCardInDTO;
  loyaltyModel: LoyaltyModelWithoutOffersDTO;
}

export type LoyaltyModelWithoutOfferFormDTO = Omit<LoyaltyModelWithoutOffersDTO, 'id'>;

export const isLoyaltyOffer = (elem: any): elem is LoyaltyOfferDTO => typeof elem?.redeemPointAmount === 'number';
